/**
 * Bridge Sentry 추적
 *
 * req·res 호출 breadcrumb, 빈 값 검증, 예외 capture를 한 곳에서 처리한다.
 */
import { SeverityLevel } from '@sentry/react'
import { handleDeviceDetect } from 'src/provider/ChannelContextProvider'
import { handleJsonParse } from 'src/utils'
import { captureError, SENTRY_SEVERITY, toBridgeError } from 'src/utils/sentry'
import { addBridgeBreadcrumb } from 'src/utils/sentry/breadcrumbs'

// ─────────────────────────────────────────────
// Bridge JSON 파싱
// ─────────────────────────────────────────────

export const parseBridgeJson = (value: string) => {
  const parsed = handleJsonParse(value)

  // 객체 여부 확인
  if (typeof parsed !== 'object' || parsed === null) {
    captureError(toBridgeError(new Error('[Bridge] JSON parse result is not an object')), {
      level: SENTRY_SEVERITY.Warning,
    })
    return null
  }

  // 빈 객체 확인
  if (Object.keys(parsed).length === 0) {
    captureError(toBridgeError(new Error('[Bridge] JSON parse result is empty object')), {
      level: SENTRY_SEVERITY.Warning,
    })
    return null
  }
  return parsed
}

// ─────────────────────────────────────────────
// Bridge 에러 Sentry 전송
// ─────────────────────────────────────────────

type BridgeBreadcrumbData = {
  bridge: string
  phase: 'req' | 'res' | 'timeout'
  channel?: 'web' | 'ios' | 'aos'
  extra?: unknown
}

function captureBridgeError(
  error: Error,
  data: BridgeBreadcrumbData,
  level: SeverityLevel = SENTRY_SEVERITY.Warning,
): void {
  const extra = (data.extra ?? {}) as Record<string, unknown>

  addBridgeBreadcrumb(
    `Bridge 실패: [${data.phase}] ${data.bridge}`,
    {
      bridge: data.bridge,
      phase: data.phase,
      ...extra,
    },
    SENTRY_SEVERITY.Error,
  )

  const { channel } = handleDeviceDetect()

  captureError(error, {
    level,
    tags: {
      bridge: data.bridge,
      bridgePhase: data.phase,
      channel,
    },
    extra,
  })
}

// ─────────────────────────────────────────────
// withBridgeTracking — req/res 래핑
// ─────────────────────────────────────────────

type AnyFn = (...args: any[]) => any
type ReqResPair = { req: AnyFn; res: AnyFn }
type Trackable = AnyFn | ReqResPair

type WrapOptions = {
  validate?: boolean
}

function isEmptyValue(value: unknown): { isEmpty: boolean; reason?: string } {
  if (value === undefined) return { isEmpty: true, reason: 'undefined' }
  if (value === null) return { isEmpty: true, reason: 'null' }
  if (value === '') return { isEmpty: true, reason: 'empty_string' }
  if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) {
    return { isEmpty: true, reason: 'empty_object' }
  }
  return { isEmpty: false }
}

function collectFailedArgKeys(slots: unknown[]): { failedKeys: string[]; reason?: string } {
  const failedKeys: string[] = []
  let reason: string | undefined

  slots.forEach((slot, i) => {
    const { isEmpty, reason: r } = isEmptyValue(slot)
    if (isEmpty) {
      failedKeys.push(`arg${i}`)
      reason ??= r
    }
  })

  return { failedKeys, reason }
}

function validateBridgeReqArgs(args: unknown[], bridgeName: string, options?: WrapOptions) {
  if (options?.validate === false) return

  const values = args.filter((a) => typeof a !== 'function')
  const { failedKeys, reason } = collectFailedArgKeys(values)

  if (!failedKeys.length) return

  captureBridgeError(
    toBridgeError(new Error(`Bridge empty value: ${bridgeName}`)),
    {
      bridge: bridgeName,
      phase: 'req',
      extra: { reason, failedKeys },
    },
    SENTRY_SEVERITY.Warning,
  )
}

function validateBridgeResValue(value: unknown, bridgeName: string, options?: WrapOptions) {
  if (options?.validate === false) return

  const { isEmpty, reason } = isEmptyValue(value)
  if (isEmpty) {
    captureBridgeError(
      toBridgeError(new Error(`Bridge empty value: ${bridgeName}`)),
      {
        bridge: bridgeName,
        phase: 'res',
        extra: { reason, failedKeys: ['arg0'] },
      },
      SENTRY_SEVERITY.Warning,
    )
    return
  }

  if (typeof value !== 'object' || value === null || Array.isArray(value)) return

  const { failedKeys, reason: fieldReason } = collectFailedArgKeys(Object.values(value))
  if (!failedKeys.length) return

  captureBridgeError(
    toBridgeError(new Error(`Bridge invalid payload: ${bridgeName}`)),
    {
      bridge: bridgeName,
      phase: 'res',
      extra: { reason: fieldReason, failedKeys },
    },
    SENTRY_SEVERITY.Warning,
  )
}

function wrapReqFn<T extends AnyFn>(fn: T, bridgeName: string, options?: WrapOptions): T {
  return ((...args: unknown[]) => {
    addBridgeBreadcrumb(`[req] ${bridgeName}`, {
      bridge: bridgeName,
      phase: 'req',
    })

    validateBridgeReqArgs(args, bridgeName, options)

    try {
      return fn(...args)
    } catch (error) {
      captureBridgeError(toBridgeError(error as Error), {
        bridge: bridgeName,
        phase: 'req',
      })
      return
    }
  }) as T
}

function wrapResFn<T>(
  fn: (callback?: (value: T) => void, failFn?: () => void) => void,
  bridgeName: string,
  options?: WrapOptions,
) {
  return (callback?: (value: T) => void, failFn?: () => void) => {
    addBridgeBreadcrumb(`[res:registered] ${bridgeName}`, {
      bridge: bridgeName,
      phase: 'res',
    })

    return fn(
      (value) => {
        addBridgeBreadcrumb(`[res] ${bridgeName}`, {
          bridge: bridgeName,
          phase: 'res',
        })

        validateBridgeResValue(value, bridgeName, options)
        callback?.(value)
      },
      () => {
        addBridgeBreadcrumb(`[res:fail] ${bridgeName}`, {
          bridge: bridgeName,
          phase: 'res',
        })
        failFn?.()
      },
    )
  }
}

export function withBridgeTracking<T extends Trackable>(
  fn: T,
  bridgeName: string,
  options?: WrapOptions,
): T {
  if (typeof fn === 'object' && fn !== null && 'req' in fn) {
    const pair = fn as ReqResPair
    return {
      req: wrapReqFn(pair.req, bridgeName, options),
      res: wrapResFn(pair.res, bridgeName, options),
    } as T
  }

  return wrapReqFn(fn as AnyFn, bridgeName, options) as T
}
